import { computed, nextTick, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import channelTemplatePresets from 'custom/api/channelTemplatePresets';
import {
  mergeProcessedParams,
  preferredTemplateMediaUrl,
  templateExampleMediaUrl,
  templateKey,
  variableDescriptors,
} from 'custom/helpers/channelTemplatePresets';

export const TEMPLATE_PRESET_PURPOSES = {
  CONVERSATION_REPLY: 'conversation_reply',
  NEW_CONVERSATION: 'new_conversation',
};

const STATUS_TONES = {
  ready: 'bg-n-teal-3 text-n-teal-11',
  review_required: 'bg-n-amber-3 text-n-amber-11',
  template_unavailable: 'bg-n-ruby-3 text-n-ruby-11',
};

export const useTemplateSendPreset = ({
  parser,
  template,
  inboxId,
  purpose,
  conversationId,
}) => {
  const { t } = useI18n();
  const loadingPreset = ref(false);
  const uploadingMedia = ref(false);
  const presetStatus = ref('not_configured');

  const statusLabels = computed(() => ({
    ready: t('CHANNEL_TEMPLATE_PRESETS.STATUS.ready'),
    not_configured: t('CHANNEL_TEMPLATE_PRESETS.STATUS.not_configured'),
    review_required: t('CHANNEL_TEMPLATE_PRESETS.STATUS.review_required'),
    template_unavailable: t(
      'CHANNEL_TEMPLATE_PRESETS.STATUS.template_unavailable'
    ),
    fallback: t('CHANNEL_TEMPLATE_PRESETS.STATUS.fallback'),
  }));
  const statusMessages = computed(() => ({
    ready: t('CHANNEL_TEMPLATE_PRESETS.STATUS_HELP.ready'),
    not_configured: t('CHANNEL_TEMPLATE_PRESETS.STATUS_HELP.not_configured'),
    review_required: t('CHANNEL_TEMPLATE_PRESETS.STATUS_HELP.review_required'),
    template_unavailable: t(
      'CHANNEL_TEMPLATE_PRESETS.STATUS_HELP.template_unavailable'
    ),
    fallback: t('CHANNEL_TEMPLATE_PRESETS.STATUS_HELP.fallback'),
  }));
  const statusLabel = computed(
    () => statusLabels.value[presetStatus.value] || statusLabels.value.fallback
  );
  const statusMessage = computed(
    () =>
      statusMessages.value[presetStatus.value] || statusMessages.value.fallback
  );
  const statusTone = computed(
    () => STATUS_TONES[presetStatus.value] || 'bg-n-slate-3 text-n-slate-11'
  );
  const defaultMediaUrl = ref('');
  const uploadedMediaUrl = ref('');
  const uploadedMediaName = ref('');

  const processedParams = computed(() => parser.value?.processedParams || {});
  const currentMediaUrl = computed(
    () => processedParams.value?.header?.media_url || ''
  );
  const metaMediaUrl = computed(() => templateExampleMediaUrl(unref(template)));
  const mediaSource = computed(() => {
    if (!currentMediaUrl.value) return '';
    if (currentMediaUrl.value === uploadedMediaUrl.value) return 'current';
    if (currentMediaUrl.value === defaultMediaUrl.value) return 'channel';
    if (currentMediaUrl.value === metaMediaUrl.value) return 'meta';
    return 'current';
  });

  const requestContext = () => ({
    purpose,
    conversationId: unref(conversationId),
  });

  const seedPreferredMedia = () => {
    const mediaUrl = preferredTemplateMediaUrl(
      unref(template),
      currentMediaUrl.value
    );
    if (mediaUrl && mediaUrl !== currentMediaUrl.value) {
      parser.value.updateMediaUrl(mediaUrl);
    }
  };

  const seedHeaderTextVariables = () => {
    const headerVariables = variableDescriptors(unref(template)).filter(
      descriptor => descriptor.section === 'header'
    );
    if (!headerVariables.length) return;

    parser.value.processedParams.header ||= {};
    headerVariables.forEach(({ key }) => {
      parser.value.processedParams.header[key] ||= '';
    });
  };

  const resolvePreset = async () => {
    loadingPreset.value = true;
    defaultMediaUrl.value = '';
    uploadedMediaUrl.value = '';
    uploadedMediaName.value = '';
    try {
      const selectedTemplate = unref(template);
      const { data } = await channelTemplatePresets.resolve(
        unref(inboxId),
        templateKey(selectedTemplate),
        {
          language: selectedTemplate.language,
          ...requestContext(),
        }
      );
      presetStatus.value = data.status;
      if (data.status === 'ready') {
        mergeProcessedParams(
          parser.value.processedParams,
          data.processed_params
        );
        defaultMediaUrl.value = data.default_media?.url || '';
        uploadedMediaName.value = data.default_media?.filename || '';
      }
      await nextTick();
      seedPreferredMedia();
      return true;
    } catch (error) {
      presetStatus.value = 'fallback';
      return false;
    } finally {
      loadingPreset.value = false;
    }
  };

  const initializePreset = async () => {
    await nextTick();
    seedHeaderTextVariables();
    return resolvePreset();
  };

  const uploadMedia = async file => {
    uploadingMedia.value = true;
    try {
      const selectedTemplate = unref(template);
      const { data } = await channelTemplatePresets.uploadMedia(
        unref(inboxId),
        templateKey(selectedTemplate),
        {
          language: selectedTemplate.language,
          file,
          ...requestContext(),
        }
      );
      parser.value.updateMediaUrl(data.url);
      if (data.filename) parser.value.updateMediaName(data.filename);
      uploadedMediaUrl.value = data.url;
      uploadedMediaName.value = data.filename || '';
      return data;
    } finally {
      uploadingMedia.value = false;
    }
  };

  const resetPreset = () => {
    presetStatus.value = 'not_configured';
    defaultMediaUrl.value = '';
    uploadedMediaUrl.value = '';
    uploadedMediaName.value = '';
  };

  return {
    currentMediaUrl,
    initializePreset,
    loadingPreset,
    mediaSource,
    presetStatus,
    processedParams,
    resolvePreset,
    resetPreset,
    statusLabel,
    statusMessage,
    statusTone,
    uploadedMediaName,
    uploadingMedia,
    uploadMedia,
  };
};
